import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "./ui/alert-dialog";
import { ArrowRight, CalendarClock, AlertTriangle } from "lucide-react";

/**
 * Confirmation dialog shown when the user drags a task on the calendar
 * to a new date. Shows old → new date/time in Italian format.
 *
 * Props:
 *   open       boolean
 *   onCancel   fn
 *   onConfirm  fn
 *   task       task obj (uses .title, .scheduled_date, .scheduled_time,
 *               .due_date)
 *   toDate     "YYYY-MM-DD"
 *   toTime     "HH:MM" | null   — optional (calendar drop doesn't set time)
 *   isOverdueDrop  boolean       — if true show extra warning (drop after due)
 */
export default function RescheduleConfirmDialog({
  open, onCancel, onConfirm, task, toDate, toTime, isOverdueDrop = false,
}) {
  if (!task) return null;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const [Y, M, D] = iso.split("-");
    return `${D}/${M}/${Y}`;
  };
  const fmtTime = (t) => (t ? t.slice(0, 5) : null);

  const oldDate = task.scheduled_date || task.due_date;
  const oldTime = fmtTime(task.scheduled_time);
  const newTime = fmtTime(toTime);

  const oldLabel = oldDate
    ? `${fmtDate(oldDate)}${oldTime ? " alle " + oldTime : ""}`
    : "senza data";
  const newLabel = `${fmtDate(toDate)}${newTime ? " alle " + newTime : ""}`;

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel?.(); }}>
      <AlertDialogContent
        className="bg-white rounded-2xl border sm:rounded-2xl"
        data-testid="reschedule-confirm-dialog"
      >
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-blue-100 p-2 text-blue-700 shrink-0">
              <CalendarClock size={20} />
            </div>
            <div className="flex-1">
              <AlertDialogTitle data-testid="reschedule-confirm-title">
                Confermi lo spostamento?
              </AlertDialogTitle>
              <AlertDialogDescription className="mt-1">
                <span className="block font-medium text-neutral-900 line-clamp-2">
                  {task.title}
                </span>
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        <div
          className="rounded-xl border bg-neutral-50 p-4 flex items-center gap-3 text-sm"
          style={{ borderColor: "var(--pxos-border, #e5e7eb)" }}
          data-testid="reschedule-confirm-transition"
        >
          <div className="flex-1 text-center">
            <div className="text-[10px] uppercase tracking-widest opacity-60">Da</div>
            <div className="font-medium mt-0.5" data-testid="reschedule-old">{oldLabel}</div>
          </div>
          <ArrowRight size={18} className="opacity-50 shrink-0" />
          <div className="flex-1 text-center">
            <div className="text-[10px] uppercase tracking-widest opacity-60">A</div>
            <div className="font-semibold mt-0.5 text-blue-800" data-testid="reschedule-new">
              {newLabel}
            </div>
          </div>
        </div>

        {isOverdueDrop && (
          <div
            className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 rounded-lg p-3 border border-amber-200"
            data-testid="reschedule-overdue-warning"
          >
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              La data programmata è <strong>dopo la scadenza</strong>
              {task.due_date && <> ({fmtDate(task.due_date)})</>}. Il ciclo risulterà comunque in ritardo.
            </div>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="reschedule-confirm-cancel">Annulla</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="reschedule-confirm-ok"
          >
            Sposta
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
